import * as React from 'react';
import { Button, NumberField, TextField, DateTimePicker } from '../common';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';
import * as moment from 'moment';
import { Moment } from 'moment';
import { DateTimeValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { Divider, Grid2 as Grid, InputLabel } from '@mui/material';

interface Props extends BaseDialogProps {
    readonly items: Entities.CompensationItem[];
    readonly user: string;
    readonly onSend: (title: string, description: string, from: number, to: number ) => void;
}

interface State extends BaseDialogState {
    readonly title: string;
    readonly description: string;
    readonly from: Moment;
    readonly to: Moment;
}

export class SendCompensationDialog extends BaseDialog<Props, State> {
    state = this.initState();

    protected renderContent(): React.JSX.Element {
        const { title, description, from, to } = this.state;
        const { items } = this.props;

        const itemViews: React.JSX.Element[] = [];
        if (items.length === 0) {
            itemViews.push(
                <Grid size={12} style={{ textAlign: 'center', fontSize: 13, color: 'grey'}}>
                    No items
                </Grid>
            );
        } else {
            items.forEach((item, index) => {
                itemViews.push(
                    <>
                        {index > 0 && (
                            <Grid size={12}>
                                <Divider />
                            </Grid>
                        )}
                        <Grid size={12}>
                            <TextField
                                label="Key"
                                fullWidth={true}
                                value={item.Key}
                                variant="outlined"
                                required={true}
                                readOnly={true}
                            />
                        </Grid>
                        <Grid size={12}>
                            <NumberField
                                label="Amount"
                                fullWidth={true}
                                value={item.Amount}
                                variant="outlined"
                                required={true}
                                readOnly={true}
                            />
                        </Grid>
                    </>
                );
            });
        }

        return (
            <Grid container={true} spacing={0}>
                <Grid size={12}>
                    <TextField
                        label="Title"
                        fullWidth={true}
                        value={title}
                        required={true}
                        onChange={this.updateTitle}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label="Description"
                        fullWidth={true}
                        value={description}
                        required={true}
                        onChange={this.updateDescription}
                        variant="outlined"
                        multiline={true}
                        rows={4}
                    />
                </Grid>
                <Grid size={12}>
                    <DateTimePicker
                        label="Start"
                        value={from}
                        onChange={this.updateFrom}
                        disablePast={false}
                    />
                </Grid>
                <Grid size={12}>
                    <DateTimePicker
                        label="End"
                        value={to}
                        onChange={this.updateTo}
                        disablePast={false}
                    />
                </Grid>

                <Grid size={12}>
                    <TextField
                        label={`User`}
                        fullWidth={true}
                        value={this.props.user}
                        variant="outlined"
                        required={true}
                        readOnly={true}
                    />
                </Grid>
                <Grid size={12}>
                    <InputLabel>Items</InputLabel>
                </Grid>
                <Grid size={12}>
                    <Grid container={true} spacing={0} style={{ width: '100%', margin: 0, padding: 10 }}>
                        {itemViews}
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    protected renderActions(): React.JSX.Element {
        return (
            <Button text="Create" onClick={this.onCreate} variant="contained" disabled={!this.validate()} />
        );
    }

    protected initState(): State {
        return {
            title: '',
            description: '',
            from: moment.parseZone(),
            to: moment.parseZone().add(1, 'month'),
        };
    }

    private readonly updateTitle = (title: string) => this.setState({ title });
    private readonly updateDescription = (description: string) => this.setState({ description });
    private readonly updateFrom = (from: Moment | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
        if (from === null) {
            return;
        }
        this.setState({ from });
    }
    private readonly updateTo = (to: Moment | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
        if (to === null) {
            return;
        }
        this.setState({ to });
    }

    private readonly validate = (): boolean => {
        const { title, description, from, to } = this.state;

        return title !== '' && description !== '' && from.isValid() && to.isValid();
    }

    private readonly onCreate = () => {
        if (!this.validate()) {
            return;
        }
        this.onCreateCompensationData();
    }

    private readonly onCreateCompensationData = () => {
        const {title, description, from, to } = this.state;
        const fromValue = from.utc().valueOf();
        const toValue = to.utc().valueOf();

        this.props.onSend(title, description, fromValue, toValue);
        this.onClose();
    };
}
